var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Dashboard widget factories — gauges.
 */
import { Arc } from '../../shapes/index';
import { syntheticEvent } from '../../components/helpers';
import { registerDashboard } from '../registryCore';
import { clamp, createWidgetGroup, getState, num, setLiveValue, setParts, setRefresh, setState, str, bool, } from '../helpers';
import { buildDialGauge, updateDialNeedle } from '../../primitives/dialGauge';
import { getActiveDashboard } from '../theme';
import { hasCustomFontSize, hasCustomTextColor, resolveNodeTypography, } from '../../components/nodeTheme';
import { normalizeDialZones, readColorStops, readDialZones, resolveValueColor, } from '../colorStops';
registerDashboard('gauge', function (props, app) {
    var size = num(props, 'size', 120);
    var max = num(props, 'max', 100);
    var value = clamp(num(props, 'value', 0), 0, max);
    var colorStops = readColorStops(props);
    var dialZones = normalizeDialZones(readDialZones(props), max);
    var group = createWidgetGroup(app, 'gauge', props, { width: size, height: size });
    var r = size / 2 - 14;
    var cx = size / 2;
    var dash = getActiveDashboard();
    var typo = resolveNodeTypography(app, props, {
        text: dash.text,
        textMuted: dash.textMuted,
        fontSize: dash.fontSizeTitle,
        fontSizeSm: dash.fontSizeSm,
        fontSizeLg: dash.fontSizeTitle,
    });
    var customFont = hasCustomFontSize(props);
    var needleColor = resolveValueColor(value, colorStops, dash.gaugeNeedle);
    var parts = buildDialGauge(app, group, {
        trackColor: dash.gaugeTrack,
        needleColor: needleColor,
        accentColor: needleColor,
        textColor: typo.text,
        textMuted: typo.textMuted,
        faceColor: dash.face,
        bezelColor: dash.panelStroke,
    }, __assign(__assign({ size: size, value: value, max: max, tickCount: 6, ariaLive: 'polite' }, (customFont ? { valueFontSize: typo.fontSize, titleFontSize: typo.fontSizeSm } : {})), (dialZones.length ? { colorZones: dialZones } : {})));
    setParts(group, { needle: parts.needle, valueText: parts.valueText, valueArc: parts.valueArc });
    setRefresh(group, function (v) {
        updateDialNeedle(parts.needle, cx, v, max, r, undefined, undefined, undefined, parts.valueArc);
        parts.valueText.text = String(Math.round(v));
        var next = resolveValueColor(v, colorStops, getActiveDashboard().gaugeNeedle);
        parts.needle.stroke = next;
        if (parts.valueArc) {
            parts.valueArc.stroke = next;
        }
    });
    setState(group, {
        size: size,
        value: value,
        max: max,
        colorStops: colorStops,
        colorZones: readDialZones(props),
        uiTheme: props.uiTheme,
        textColor: props.textColor,
        color: props.color,
        textMuted: props.textMuted,
        fontSize: props.fontSize,
        demoId: props.demoId,
        hasCustomTextColor: hasCustomTextColor(props),
        hasCustomFontSize: customFont,
    });
    if (props.demoId != null)
        group.metadata.demoId = props.demoId;
    return group;
});
registerDashboard('speedometer', function (props, app) {
    var size = num(props, 'size', 200);
    var value = num(props, 'value', 0);
    var max = num(props, 'max', 180);
    var colorStops = readColorStops(props);
    var dialZones = normalizeDialZones(readDialZones(props), max);
    var group = createWidgetGroup(app, 'speedometer', props);
    var r = size / 2 - 14;
    var cx = size / 2;
    var dash = getActiveDashboard();
    var typo = resolveNodeTypography(app, props, {
        text: dash.text,
        textMuted: dash.textMuted,
        fontSize: dash.fontSizeTitle,
        fontSizeSm: dash.fontSizeSm,
        fontSizeLg: dash.fontSizeTitle,
    });
    var customFont = hasCustomFontSize(props);
    var needleColor = resolveValueColor(value, colorStops, dash.speedoNeedle);
    var parts = buildDialGauge(app, group, {
        trackColor: dash.gaugeTrack,
        needleColor: needleColor,
        accentColor: needleColor,
        textColor: typo.text,
        textMuted: typo.textMuted,
        faceColor: dash.face,
        bezelColor: dash.panelStroke,
        redlineColor: dash.dangerDark,
    }, __assign(__assign({ size: size, value: value, max: max, unit: str(props, 'unit', 'km/h'), tickCount: 9, showTickLabels: true }, (customFont
        ? { valueFontSize: typo.fontSize, unitFontSize: typo.fontSizeSm }
        : {})), (dialZones.length ? { colorZones: dialZones } : { redlineFrom: 0.78 })));
    setParts(group, { needle: parts.needle, valueText: parts.valueText, valueArc: parts.valueArc });
    setRefresh(group, function (v) {
        updateDialNeedle(parts.needle, cx, v, max, r, undefined, undefined, undefined, parts.valueArc);
        parts.valueText.text = "".concat(Math.round(v));
        var next = resolveValueColor(v, colorStops, getActiveDashboard().speedoNeedle);
        parts.needle.stroke = next;
        if (parts.valueArc) {
            parts.valueArc.stroke = next;
        }
    });
    setState(group, {
        size: size,
        value: value,
        max: max,
        unit: str(props, 'unit', 'km/h'),
        colorStops: colorStops,
        colorZones: readDialZones(props),
        uiTheme: props.uiTheme,
        textColor: props.textColor,
        color: props.color,
        textMuted: props.textMuted,
        fontSize: props.fontSize,
        hasCustomTextColor: hasCustomTextColor(props),
        hasCustomFontSize: customFont,
    });
    return group;
});
registerDashboard('meter', function (props, app) {
    var width = num(props, 'width', 200);
    var height = num(props, 'height', 24);
    var value = clamp(num(props, 'value', 60), 0, 100);
    var vertical = bool(props, 'vertical', false);
    var colorStops = readColorStops(props);
    var fillColor = resolveValueColor(value, colorStops, getActiveDashboard().meterFill);
    var group = createWidgetGroup(app, 'meter', props);
    if (vertical) {
        group.add(app.rect({ width: height, height: width, fill: getActiveDashboard().meterTrack, listening: false }));
        var fillBar_1 = app.rect({
            x: 2,
            y: width - (width * value) / 100 - 2,
            width: height - 4,
            height: (width * value) / 100,
            fill: fillColor,
            listening: false,
        });
        group.add(fillBar_1);
        setRefresh(group, function (v) {
            var pct = clamp(v, 0, 100) / 100;
            fillBar_1.y = width - width * pct - 2;
            fillBar_1.height = width * pct;
            fillBar_1.fill = resolveValueColor(clamp(v, 0, 100), colorStops, getActiveDashboard().meterFill);
        });
    }
    else {
        var trackR = Math.min(4, height / 2);
        group.add(app.roundedRect({
            width: width,
            height: height,
            cornerRadius: trackR,
            fill: getActiveDashboard().meterTrack,
            listening: false,
        }));
        var fillBar_2 = app.roundedRect({
            x: 0,
            y: 0,
            width: (width * value) / 100,
            height: height,
            cornerRadius: trackR,
            fill: fillColor,
            listening: false,
        });
        group.add(fillBar_2);
        setRefresh(group, function (v) {
            var clamped = clamp(v, 0, 100);
            fillBar_2.width = (width * clamped) / 100;
            fillBar_2.fill = resolveValueColor(clamped, colorStops, getActiveDashboard().meterFill);
        });
    }
    setState(group, { width: width, height: height, value: value, vertical: vertical, colorStops: colorStops });
    return group;
});
registerDashboard('knob', function (props, app) {
    var size = num(props, 'size', 80);
    var value = clamp(num(props, 'value', 50), 0, 100);
    var group = createWidgetGroup(app, 'knob', props, { width: size, height: size, focusable: true, listening: true });
    var cx = size / 2;
    var r = size / 2 - 5;
    var start = Math.PI * 0.75;
    var sweep = Math.PI * 1.5;
    var angle = start + (value / 100) * sweep;
    var arcW = Math.max(3, size * 0.07);
    var ptrR = r - arcW;
    group.add(app.circle({
        x: cx - r,
        y: cx - r,
        radius: r,
        fill: getActiveDashboard().knobTrack,
        stroke: getActiveDashboard().knobRing,
        strokeWidth: Math.max(1.5, size / 40),
        shadow: size >= 48 ? { color: 'rgba(0,0,0,0.35)', blur: 5, offsetX: 0, offsetY: 2 } : undefined,
        listening: false,
    }));
    group.add(new Arc({
        x: cx - r + arcW,
        y: cx - r + arcW,
        radius: r - arcW,
        startAngle: start,
        endAngle: start + sweep,
        fill: null,
        stroke: getActiveDashboard().inactive,
        strokeWidth: arcW * 0.65,
        listening: false,
    }));
    var valueArc = new Arc({
        x: cx - r + arcW,
        y: cx - r + arcW,
        radius: r - arcW,
        startAngle: start,
        endAngle: angle,
        fill: null,
        stroke: getActiveDashboard().knobIndicator,
        strokeWidth: arcW,
        listening: false,
    });
    group.add(valueArc);
    var ptrSize = Math.max(4, size * 0.09);
    var pointer = app.circle({
        x: cx + ptrR * Math.cos(angle) - ptrSize,
        y: cx + ptrR * Math.sin(angle) - ptrSize,
        radius: ptrSize,
        fill: getActiveDashboard().knobIndicator,
        stroke: '#fff',
        strokeWidth: 1,
        listening: false,
    });
    var valueLabel = app.text({
        text: String(Math.round(value)),
        x: cx,
        y: cx,
        fontSize: Math.max(10, size * 0.22),
        fontWeight: '600',
        fill: getActiveDashboard().text,
        textAlign: 'center',
        textBaseline: 'middle',
        listening: false,
    });
    group.add(pointer, valueLabel);
    setParts(group, { valueArc: valueArc, pointer: pointer, valueLabel: valueLabel });
    setRefresh(group, function (v) {
        var pct = clamp(v, 0, 100) / 100;
        var a = start + pct * sweep;
        valueArc.endAngle = a;
        pointer.x = cx + ptrR * Math.cos(a) - ptrSize;
        pointer.y = cx + ptrR * Math.sin(a) - ptrSize;
        valueLabel.text = String(Math.round(clamp(v, 0, 100)));
    });
    group.on('click', function () {
        var next = (num(getState(group), 'value', 0) + 10) % 100;
        setLiveValue(group, 'value', next);
        group.emit('change', syntheticEvent('change', group, { value: next }));
    });
    setState(group, { size: size, value: value });
    return group;
});
registerDashboard('battery', function (props, app) {
    var _a;
    var level = clamp(num(props, 'value', 75), 0, 100);
    var scale = num(props, 'scale', 1);
    var colorStops = (_a = readColorStops(props)) !== null && _a !== void 0 ? _a : [
        { upTo: 20, color: 'danger' },
        { color: 'success' },
    ];
    var bodyW = Math.round(40 * scale);
    var bodyH = Math.round(20 * scale);
    var group = createWidgetGroup(app, 'battery', props);
    group.add(app.roundedRect({
        width: bodyW,
        height: bodyH,
        cornerRadius: Math.max(2, 3 * scale),
        fill: null,
        stroke: getActiveDashboard().batteryOutline,
        strokeWidth: Math.max(1.5, 2 * scale),
        listening: false,
    }));
    group.add(app.roundedRect({
        x: bodyW,
        y: bodyH * 0.3,
        width: Math.max(3, 4 * scale),
        height: bodyH * 0.4,
        cornerRadius: 1,
        fill: getActiveDashboard().batteryTip,
        listening: false,
    }));
    var inset = Math.max(2, 2 * scale);
    var fill = app.roundedRect({
        x: inset,
        y: inset,
        width: ((bodyW - inset * 2) * level) / 100,
        height: bodyH - inset * 2,
        cornerRadius: Math.max(1, 2 * scale),
        fill: resolveValueColor(level, colorStops, getActiveDashboard().success),
        listening: false,
    });
    group.add(fill);
    setRefresh(group, function (v) {
        var lv = clamp(v, 0, 100);
        fill.width = ((bodyW - inset * 2) * lv) / 100;
        fill.fill = resolveValueColor(lv, colorStops, getActiveDashboard().success);
    });
    setState(group, {
        value: level,
        scale: scale,
        width: bodyW + Math.max(3, 4 * scale),
        height: bodyH,
        colorStops: readColorStops(props),
    });
    return group;
});
